import { isNil } from 'lodash';
import {
  MAP_OUTCOME_TO_PRETTY_LABEL,
  OutcomeCode,
} from '../enums/hitters.enums';
import { Nullable } from '../interfaces/common/i-generics';
import { IStrikeZone } from '../interfaces/i-strike-zone';
import {
  EMPTY_SESSION_HIT_DETAILS,
  ISHHitDetails,
} from '../interfaces/session-hits/i-hit-details';
import { ISessionHit } from '../interfaces/session-hits/i-session-hit';
import { gaussianRandom, isBetween } from './math.utilities';
import { PlateHelper } from './plate.helper';

export class HitHelper {
  /**
   * general priority listing:
   * - unknown (shouldn't trigger, bad data)
   * - foul (easiest to rule out definitively)
   * - ground outs
   * - fly outs
   * - home run
   * - triple
   * - double
   * - single
   * - unknown (shouldn't trigger, we couldn't figure out what happened)
   */
  static getOutcomeLegacy(hit: Partial<ISHHitDetails>): OutcomeCode {
    if (isNil(hit.distanceFT)) {
      return OutcomeCode.Unknown;
    }

    // Rapsodo may report 0 distance for hits
    if (!hit.distanceFT) {
      return OutcomeCode.Unknown;
    }

    if (isNil(hit.hLaunchDEG)) {
      return OutcomeCode.Unknown;
    }

    if (isNil(hit.vLaunchDEG)) {
      return OutcomeCode.Unknown;
    }

    if (isNil(hit.exitMPH)) {
      return OutcomeCode.Unknown;
    }

    // FOUL TERRITORY - Check foul first (horizontal launch angle > 45°)
    if (Math.abs(hit.hLaunchDEG) > 45) {
      // All foul balls are labeled as foul for now
      // TODO: Implement proper catchability logic based on foul territory boundaries
      return OutcomeCode.Foul;

      // Previous logic (commented out to preserve):
      // Long foul balls that could be caught
      // if (hit.distanceFT > 140) {
      //   return OutcomeCode.FoulOut;
      // }
      // Short foul balls
      // return OutcomeCode.Foul;
    }

    // FAIR TERRITORY - Process from most valuable outcomes down
    const absHLaunch = Math.abs(hit.hLaunchDEG);

    // GROUND OUTS - Low launch angle with decent exit velocity (check before doubles)
    if (hit.vLaunchDEG < 8 && hit.exitMPH > 65) {
      return OutcomeCode.GroundOut;
    }

    // Weak contact handling
    if ((hit.exitMPH <= 65 || hit.distanceFT < 50) && hit.vLaunchDEG < 12) {
      return OutcomeCode.GroundOut;
    }

    // FLY OUTS - High launch angle cases (check before doubles to catch warning track shots)
    // high fly
    if (hit.vLaunchDEG >= 40 && hit.distanceFT < 300) {
      return OutcomeCode.FlyOut;
    }

    // warning track
    if (
      isBetween(hit.vLaunchDEG, 30, 45) &&
      isBetween(hit.distanceFT, 220, 280)
    ) {
      return OutcomeCode.FlyOut;
    }

    // medium high fly
    if (
      isBetween(hit.vLaunchDEG, 35, 50) &&
      isBetween(hit.distanceFT, 150, 220)
    ) {
      return OutcomeCode.FlyOut;
    }

    // Corner warning track
    if (absHLaunch > 35 && isBetween(hit.distanceFT, 280, 335)) {
      return OutcomeCode.FlyOut;
    }

    // very high pop up
    if (hit.vLaunchDEG > 50) {
      return OutcomeCode.FlyOut;
    }

    if ((hit.exitMPH <= 65 || hit.distanceFT < 50) && hit.vLaunchDEG >= 12) {
      return OutcomeCode.FlyOut;
    }

    // HOME RUNS - Account for field position with realistic distances
    if (isBetween(hit.vLaunchDEG, 20, 50) && hit.exitMPH > 90) {
      // Center field (straight away) - 400ft required
      if (absHLaunch <= 15) {
        if (hit.distanceFT >= 400) {
          return OutcomeCode.HomeRun;
        }

        // Not far enough for center field HR - it's a deep fly out
        return OutcomeCode.FlyOut;
      }

      // Corner/foul line territory - 325ft required
      if (isBetween(absHLaunch, 35, 45) && hit.distanceFT >= 325) {
        return OutcomeCode.HomeRun;
      }

      // If good trajectory but not far enough, it's a fly out
      if (hit.distanceFT >= 250) {
        return OutcomeCode.FlyOut;
      }
    }

    // TRIPLES - Account for fielding position difficulty (based on MLB data)
    if (isBetween(hit.vLaunchDEG, 15, 35) && hit.exitMPH > 90) {
      // Right field - 45.7% of MLB triples, easier due to long throw to 3B
      if (hit.hLaunchDEG >= 25 && hit.distanceFT >= 260) {
        return OutcomeCode.Triple;
      }

      // Center field - 41.7% of MLB triples, standard difficulty
      if (isBetween(hit.hLaunchDEG, -5, 15) && hit.distanceFT >= 280) {
        return OutcomeCode.Triple;
      }

      // Left field - Only 12.6% of MLB triples, harder due to short throw to 3B
      if (hit.hLaunchDEG <= -25 && hit.distanceFT >= 320) {
        return OutcomeCode.Triple;
      }

      // Power alleys (between center and corners) - 350ft required
      if (isBetween(absHLaunch, 15, 35) && hit.distanceFT >= 350) {
        return OutcomeCode.Triple;
      }

      // Power alleys - medium difficulty
      if (isBetween(absHLaunch, 15, 25) && hit.distanceFT >= 280) {
        return OutcomeCode.Triple;
      }
    }

    // DOUBLES - Longer distance with good trajectory (more selective)
    if (
      hit.distanceFT >= 240 &&
      isBetween(hit.vLaunchDEG, 8, 30) &&
      hit.exitMPH > 80
    ) {
      return OutcomeCode.Double;
    }

    // SINGLES - Everything else that's in fair territory with reasonable contact
    if (hit.distanceFT >= 50 && hit.vLaunchDEG >= 5 && hit.exitMPH >= 45) {
      return OutcomeCode.Single;
    }

    return OutcomeCode.Unknown;
  }

  static getOutcome(
    sessionHit: Partial<ISessionHit>,
    strikeZone: IStrikeZone
  ): OutcomeCode | undefined {
    const { hit, pitch } = sessionHit;

    if (hit?._mx_outcome) {
      // always use the mx_outcome if it exists and is not unknown (i.e. "")
      return hit._mx_outcome;
    }

    if (!hit) {
      // shouldn't trigger but just in case
      if (pitch?.plate) {
        // without a hit, there is no swing flag => use plate location to determine the result
        const isStrike = PlateHelper.isStrike({
          plateFt: pitch.plate,
          szDef: strikeZone,
        });

        return isStrike ? OutcomeCode.CalledStrike : OutcomeCode.Ball;
      }

      // without a hit or plate location => impossible to determine the result
      return undefined;
    }

    // hit exists, but outcome from TDS model is empty => resolve to swinging strike or called strike based on plate location
    if (hit._mx_swing) {
      // plate location doesn't matter if the hitter swung
      return OutcomeCode.SwingingStrike;
    }

    if (pitch?.plate) {
      // if the hitter did not swing, use plate location to determine the result
      const isStrike = PlateHelper.isStrike({
        plateFt: pitch.plate,
        szDef: strikeZone,
      });

      return isStrike ? OutcomeCode.CalledStrike : OutcomeCode.Ball;
    }

    // no hit, no plate => unknown outcome
    return undefined;
  }

  static getOutcomeColor(value: OutcomeCode, fallback = 'var(--gray-9)') {
    switch (value) {
      case OutcomeCode.Single: {
        return '#F76B15';
      }

      case OutcomeCode.Double: {
        return '#6E56CF';
      }

      case OutcomeCode.Triple: {
        return '#FFE629';
      }

      case OutcomeCode.HomeRun: {
        return '#D6409F';
      }

      case OutcomeCode.Ball: {
        return '#0090FF';
      }

      case OutcomeCode.Foul: {
        return '#AD7F58';
      }

      case OutcomeCode.FoulOut: {
        return '#6E6E6E';
      }

      case OutcomeCode.FlyOut: {
        return '#5B8FA8';
      }

      case OutcomeCode.GroundOut: {
        return '#8B8D98';
      }

      case OutcomeCode.SwingingStrike: {
        return '#EC5D5E';
      }

      case OutcomeCode.CalledStrike: {
        return '#CE2C31';
      }

      case OutcomeCode.Unknown:
      default: {
        return fallback;
      }
    }
  }

  static getOutcomeLabel(value: Nullable<OutcomeCode>) {
    if (!value) {
      return 'Unknown';
    }

    return MAP_OUTCOME_TO_PRETTY_LABEL[value];
  }

  // based on Ohtani's averages for 2024
  static generateSampleData(withOutcome?: boolean): ISHHitDetails {
    const didSwing = gaussianRandom(0, 1) < 0.75;
    const didHit = didSwing ? gaussianRandom(0, 1) < 0.75 : false;

    const vLaunchDEG = didHit ? gaussianRandom(16, 10) : 0;
    const exitMPH = didHit ? gaussianRandom(95, 10) : 0;

    const o: ISHHitDetails = {
      ...EMPTY_SESSION_HIT_DETAILS,
      distanceFT: didHit
        ? vLaunchDEG > 16
          ? gaussianRandom(exitMPH * 3, 20)
          : gaussianRandom(exitMPH * 2, 10)
        : 0,
      hLaunchDEG: didHit ? gaussianRandom(0, 30) : 0,
      vLaunchDEG: vLaunchDEG,
      exitMPH: exitMPH,
      swing: didSwing,
      _mx_swing: didSwing,
    };

    if (withOutcome) {
      o.outcome = this.getOutcomeLegacy(o);
      o._mx_outcome = o.outcome;
    }

    return o;
  }

  // a "safe" hit is one whose metrics we trust for max/avg
  static isSafeHit(hit: ISessionHit): boolean {
    return (
      !!hit.avg_excluded === false &&
      !!hit.hit?.invalid === false &&
      !!hit.isInGame === false &&
      (hit.hit?.distanceFT ?? 0) > 0 // contact made
    );
  }
}
